import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { usePracticeDetails } from "features/practice/hooks/usePracticeDetails";

import {
  generalInformationFormSchema,
  lungsAdditionalInfoFormSchema,
  patientDetailsFormSchema,
  prostateAdditionalInfoFormSchema,
  referralFormSchema,
} from "../schemas";
import {
  getGeneralInformationDefaultValues,
  getLungsDefaultValues,
  getPatientDetailsDefaultValues,
  getProstateDefaultValues,
  getReferralFormDefaultValues,
} from "../utils/referralDefaults";

export const useReferralForms = ({
  patient,
  patientPreferences,
  patientSocial,
  patientBaseline,
  medicalHistory,
  allergies,
  familyMedicalHistory,
  staffMember,
  consultationsList,
  today,
}) => {
  const activeConsultation = patient?.activeConsultationId
    ? consultationsList?.find(
        (consultation) => consultation.id === patient.activeConsultationId
      )
    : null;

  const generalInformationForm = useForm({
    resolver: zodResolver(generalInformationFormSchema),
    defaultValues: getGeneralInformationDefaultValues(),
  });

  const referralType = generalInformationForm.watch("referralType");
  const isLungsReferral = referralType?.toLowerCase().includes("lung");
  const isProstateReferral = referralType?.toLowerCase().includes("prostate");

  const prostateAdditionalInfoForm = useForm({
    resolver: zodResolver(prostateAdditionalInfoFormSchema),
    defaultValues: getProstateDefaultValues(today),
  });

  const lungsAdditionalInfoForm = useForm({
    resolver: zodResolver(lungsAdditionalInfoFormSchema),
    defaultValues: getLungsDefaultValues(),
  });

  const { data: practiceDetails } = usePracticeDetails();
  const practiceOutOfHoursPhone = practiceDetails?.at(0)?.outOfHoursPhone || "";

  const patientDetailsForm = useForm({
    resolver: zodResolver(patientDetailsFormSchema),
    defaultValues: getPatientDetailsDefaultValues({
      staffMember,
      patient,
      patientPreferences,
      practiceOutOfHoursPhone,
    }),
  });

  // Practice details can arrive after the form has mounted, and defaultValues are read only once.
  // The practice's number is what gets sent, so it replaces whatever the field started with.
  useEffect(() => {
    if (practiceOutOfHoursPhone) {
      patientDetailsForm.setValue("gpOutofHoursNumber", practiceOutOfHoursPhone, {
        shouldValidate: patientDetailsForm.formState.isSubmitted,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceOutOfHoursPhone]);

  const referralForm = useForm({
    resolver: async (data, context, options) => {
      return zodResolver(
        referralFormSchema(isLungsReferral, isProstateReferral)
      )(data, context, options);
    },
    defaultValues: getReferralFormDefaultValues({
      activeConsultation,
      medicalHistory,
      allergies,
      familyMedicalHistory,
      patient,
      patientSocial,
      patientBaseline,
      isProstateReferral,
      isLungsReferral,
    }),
  });

  return {
    generalInformationForm,
    patientDetailsForm,
    referralForm,
    prostateAdditionalInfoForm,
    lungsAdditionalInfoForm,
    referralType,
    practiceOutOfHoursPhone,
    isLungsReferral,
    isProstateReferral,
  };
};
